/**
 * Work-log entry derivation (ticket 3525, upstream pin e3e7cc3f, MIT): the
 * quiet-timeline entry vocabulary (turnId, toolCallId, lifecycle status,
 * source activity kind, agent spawn fields, question answers, tool
 * presentation) merged into forge's existing detail pipeline (ticket 3522).
 * Forge deltas kept on purpose: the tone union includes "approval" and keeps
 * it (upstream folds approval rows to "info"), the brokered GitHub blocker
 * promotion stays, and the runtime diagnostics detail pipeline stays.
 *
 * @module workLogEntryDerivation
 */

import type {
  OrchestrationThreadActivity,
  ToolLifecycleItemType,
  TurnId,
} from "@cmmd-forge/contracts";
import { extractToolCommand, stripTrailingExitCode } from "./workLogCommand";
import {
  extractToolDetail,
  extractWorkLogItemType,
  runtimeDiagnosticDetail,
} from "./workLogDetail";
import { extractToolTitle } from "./workLogBrowserTitle";
import {
  extractGitHubActionRequiredRequest,
  extractGitHubConfirmationRequest,
  formatGitHubActionRequiredLabel,
  formatGitHubConfirmationDetail,
  formatGitHubOperationLabel,
  resolveGitHubActionRequiredDetail,
  type GitHubActionRequiredRequest,
  type GitHubToolConfirmationRequest,
} from "./workLogGitHub";
import { isUserInputAnswerPayload } from "./workLogUserInput";
import {
  extractToolActivityPresentation,
  type ToolActivityIcon,
  type ToolActivitySource,
  type ToolActivitySurface,
} from "./workLogToolActivityPresentation";
import { deriveToolLifecycleCollapseKey } from "./workLogEntryCollapse";
import {
  extractWorkLogToolLifecycleStatus,
  workEntryIndicatesToolFailure,
  workEntryIndicatesToolSuccess,
  workLogEntryIsToolLike,
  type WorkLogToolLifecycleStatus,
} from "./workLogToolPresentation";
import {
  asRecord,
  asTrimmedString,
  extractChangedFiles,
  extractToolCallId,
  extractWorkLogRequestKind,
  type WorkLogRequestKind,
} from "./workLogEntryExtractors";

export type {
  GitHubActionRequiredRequest,
  GitHubToolConfirmationRequest,
  ToolActivityIcon,
  ToolActivitySource,
  ToolActivitySurface,
  WorkLogToolLifecycleStatus,
};

export interface WorkLogEntry {
  questionAnswer?: {
    requestId: string;
    questionTextById?: Record<string, string> | undefined;
    answers: Readonly<Record<string, unknown>>;
    attachmentsByQuestionId: Readonly<
      Record<string, ReadonlyArray<{ id: string; name: string; type?: string }>>
    >;
  };
  id: string;
  createdAt: string;
  turnId?: TurnId | null;
  /** Stable provider identity across in-progress and completed lifecycle updates. */
  toolCallId?: string;
  label: string;
  detail?: string;
  viewedImagePath?: string;
  command?: string;
  rawCommand?: string;
  changedFiles?: ReadonlyArray<string>;
  tone: "thinking" | "tool" | "info" | "error" | "approval";
  toolTitle?: string;
  toolSurface?: ToolActivitySurface;
  toolIcon?: ToolActivityIcon;
  toolSource?: ToolActivitySource;
  toolData?: unknown;
  itemType?: ToolLifecycleItemType;
  requestKind?: WorkLogRequestKind;
  /** From runtime item / task payload `status` when present (e.g. tool.updated). */
  toolLifecycleStatus?: WorkLogToolLifecycleStatus;
  /** Originating orchestration activity kind (e.g. `user-input.requested`) for row chrome. */
  sourceActivityKind?: OrchestrationThreadActivity["kind"];
  /** Grouping key for subagent lifecycle rows (one row per agent). */
  taskId?: string;
  /** Agent role (subagent_type) for labeled timeline rows. */
  agentRole?: string;
  /**
   * Present on agent-spawn rows: one per workflow run or per-turn batch of
   * direct spawns. The row ("Kicked off N subagents") derives its live
   * status and member list from the agent panel model at render time.
   */
  agentSpawn?: {
    /** Workflow coordinator taskId, or null for a direct-spawn batch. */
    workflowId: string | null;
    agentTaskIds: ReadonlyArray<string>;
  };
  githubConfirmation?: GitHubToolConfirmationRequest;
  githubActionRequired?: GitHubActionRequiredRequest;
}

export interface DerivedWorkLogEntry extends WorkLogEntry {
  sourceActivityKind: OrchestrationThreadActivity["kind"];
  collapseKey?: string;
  isWorkflowCoordinator?: boolean;
  /** Shell/monitor/plan tasks: ordinary work-log rows, never spawn CTAs. */
  isBackgroundTask?: boolean;
}

const derivedWorkLogEntryByActivity = new WeakMap<
  OrchestrationThreadActivity,
  DerivedWorkLogEntry
>();

/** Tool-like row with neither clear success nor failure (empty, incomplete, in progress, etc.). */
export function workEntryIndicatesToolNeutralStatus(entry: WorkLogEntry): boolean {
  // Spawn CTA rows are never neutral-hidden: mid-run they derive from
  // task.progress (tone "thinking") and the neutral filter was swallowing
  // them exactly while the fleet ran — the one moment they matter most.
  if (entry.agentSpawn !== undefined) {
    return false;
  }
  if (!workLogEntryIsToolLike(entry)) {
    return false;
  }
  if (workEntryIndicatesToolFailure(entry)) {
    return false;
  }
  if (workEntryIndicatesToolSuccess(entry)) {
    return false;
  }
  return true;
}

// fallow-ignore-next-line complexity -- port of upstream session-logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function toDerivedWorkLogEntry(activity: OrchestrationThreadActivity): DerivedWorkLogEntry {
  const cachedEntry = derivedWorkLogEntryByActivity.get(activity);
  if (cachedEntry) {
    return cachedEntry;
  }
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  const commandPreview = extractToolCommand(payload);
  const changedFiles = extractChangedFiles(payload);
  const title = extractToolTitle(payload);
  const toolPresentation = extractToolActivityPresentation(payload);
  const isTaskActivity = activity.kind === "task.progress" || activity.kind === "task.completed";
  const taskSummary =
    isTaskActivity && typeof payload?.summary === "string" && payload.summary.length > 0
      ? payload.summary
      : null;
  const taskDetailAsLabel =
    isTaskActivity &&
    !taskSummary &&
    typeof payload?.detail === "string" &&
    payload.detail.length > 0
      ? payload.detail
      : null;
  const taskLabel = taskSummary || taskDetailAsLabel;
  // Upstream detail pipeline (ticket 3522): task rows keep their payload
  // detail; every other row resolves its detail through the command-output
  // pipeline, so a command row shows its output instead of echoing the
  // command it ran.
  const detail = isTaskActivity
    ? !taskDetailAsLabel &&
      payload &&
      typeof payload.detail === "string" &&
      payload.detail.length > 0
      ? stripTrailingExitCode(payload.detail).output
      : null
    : extractToolDetail(payload, title ?? activity.summary);
  const toolCallId = isTaskActivity ? null : extractToolCallId(payload);
  const entry: DerivedWorkLogEntry = {
    id: activity.id,
    createdAt: activity.createdAt,
    turnId: activity.turnId,
    label: taskLabel || activity.summary,
    tone: activity.kind === "task.progress" ? "thinking" : activity.tone,
    sourceActivityKind: activity.kind,
  };
  if (activity.kind === "user-input.answer-submitted") {
    const answer = payload && isUserInputAnswerPayload(payload) ? payload : null;
    if (answer) {
      entry.questionAnswer = {
        requestId: answer.requestId,
        questionTextById: answer.questionTextById,
        answers: answer.answers,
        attachmentsByQuestionId: answer.attachmentsByQuestionId,
      };
    }
  }
  const itemType = extractWorkLogItemType(payload);
  const requestKind = extractWorkLogRequestKind(payload);
  const viewedImagePath = asTrimmedString(asRecord(payload?.data)?.imagePath);
  if (detail) {
    entry.detail = detail;
  } else if (activity.kind === "runtime.error" || activity.kind === "runtime.warning") {
    // Forge keeps its runtime diagnostics pipeline (ticket 3522): the
    // retained provider message reaches `detail` beyond the truncated summary.
    const diagnostic = runtimeDiagnosticDetail(payload, activity.summary);
    if (diagnostic) {
      entry.detail = diagnostic;
    }
  }
  if (viewedImagePath) {
    entry.viewedImagePath = viewedImagePath;
  }
  if (commandPreview.command) {
    entry.command = commandPreview.command;
  }
  if (commandPreview.rawCommand) {
    entry.rawCommand = commandPreview.rawCommand;
  }
  if (changedFiles.length > 0) {
    entry.changedFiles = changedFiles;
  }
  if (title) {
    entry.toolTitle = title;
  }
  if (toolPresentation.toolSurface) {
    entry.toolSurface = toolPresentation.toolSurface;
  }
  if (toolPresentation.toolIcon) {
    entry.toolIcon = toolPresentation.toolIcon;
  }
  if (toolPresentation.toolSource) {
    entry.toolSource = toolPresentation.toolSource;
  }
  if (itemType === "mcp_tool_call") {
    const data = asRecord(payload?.data);
    const toolData = typeof data?.toolName === "string" ? (data.item ?? data) : data?.item;
    if (toolData !== undefined) {
      entry.toolData = toolData;
    }
  }
  if (itemType) {
    entry.itemType = itemType;
  }
  if (requestKind) {
    entry.requestKind = requestKind;
  }
  if (toolCallId) {
    entry.toolCallId = toolCallId;
  }
  let toolLifecycleStatus = extractWorkLogToolLifecycleStatus(payload);
  if (!toolLifecycleStatus && activity.kind === "tool.completed") {
    toolLifecycleStatus = "completed";
  }
  if (toolLifecycleStatus) {
    entry.toolLifecycleStatus = toolLifecycleStatus;
  }
  const isLifecycleTaskActivity =
    activity.kind === "task.started" ||
    activity.kind === "task.progress" ||
    activity.kind === "task.completed";
  if (isLifecycleTaskActivity && typeof payload?.taskId === "string" && payload.taskId.length > 0) {
    entry.taskId = payload.taskId;
  }
  if (isLifecycleTaskActivity && typeof payload?.role === "string" && payload.role.length > 0) {
    entry.agentRole = payload.role;
  }
  if (
    isLifecycleTaskActivity &&
    (payload?.taskType === "local_workflow" ||
      (typeof payload?.workflowName === "string" && payload.workflowName.length > 0))
  ) {
    entry.isWorkflowCoordinator = true;
  }
  if (isLifecycleTaskActivity && payload && payload.agentKind !== "agent") {
    entry.isBackgroundTask = true;
  }
  // Forge promotion (kept): brokered GitHub blockers become approval rows.
  const githubConfirmation = extractGitHubConfirmationRequest(payload);
  if (githubConfirmation) {
    entry.githubConfirmation = githubConfirmation;
    entry.tone = "approval";
    entry.label = "GitHub action needs approval";
    entry.toolTitle = formatGitHubOperationLabel(githubConfirmation.operation);
    entry.detail = formatGitHubConfirmationDetail(githubConfirmation);
  } else {
    const githubActionRequired = extractGitHubActionRequiredRequest(payload);
    if (githubActionRequired) {
      entry.githubActionRequired = githubActionRequired;
      entry.tone = "approval";
      entry.label = formatGitHubActionRequiredLabel(githubActionRequired.action);
      entry.toolTitle = "GitHub setup required";
      entry.detail = resolveGitHubActionRequiredDetail(githubActionRequired);
    }
  }
  const collapseKey = deriveToolLifecycleCollapseKey(entry);
  if (collapseKey) {
    entry.collapseKey = collapseKey;
  }
  derivedWorkLogEntryByActivity.set(activity, entry);
  return entry;
}
